import { Aggregates, Category, Operation, Settings } from '../api/types';
import { computeDailyBudget } from './daily-budget';

/**
 * Процессный стрик: «дни, когда ты вносил записи» (по created_at,
 * т.е. награждается само действие записи, а не дата траты).
 */

function toLocalIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function shiftIso(iso: string, days: number): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days);
  return toLocalIso(d);
}

/** created_at (ISO или SQLite "YYYY-MM-DD HH:MM:SS" UTC) → локальный день YYYY-MM-DD. */
export function createdAtLocalDay(createdAt: string): string | null {
  if (!createdAt) return null;
  let s = createdAt.trim();
  if (s.includes(' ') && !s.includes('T')) s = s.replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return toLocalIso(d);
}

/**
 * Дни, в которые реально велись записи. Импортированные (imp_*) не считаются.
 *
 * `aggregates.recorded_days` — серверный список дней по ВСЕЙ истории (план 3a),
 * окно `operations` его не покрывает. Берём ОБЪЕДИНЕНИЕ, а не замену: локальная
 * оптимистичная запись ещё не попала в агрегат прошлого bootstrap, но день уже
 * должен гореть. Сервер считает день по МСК (`_lib/aggregates.ts`), клиент — по
 * локальному TZ; household живёт в МСК, поэтому оба дают один и тот же день.
 */
export function recordedDaySet(
  operations: Operation[],
  aggregates?: Pick<Aggregates, 'recorded_days'> | null,
): Set<string> {
  const set = new Set<string>(aggregates?.recorded_days ?? []);
  for (const op of operations) {
    if (op.id.startsWith('imp_')) continue;
    const day = createdAtLocalDay(op.created_at);
    if (day) set.add(day);
  }
  return set;
}

export interface RecordingStreak {
  current: number;
  best: number;
  /** Сегодня уже есть записи (день «горит»). */
  todayDone: boolean;
}

/**
 * Текущий стрик дней с записями. Сегодняшний день засчитывается,
 * если записи уже есть; если нет — стрик не рвётся (день ещё идёт),
 * счёт начинается со вчера. Дни из frozen (❄ заморозки) считаются закрытыми.
 *
 * Функция ЧИСТАЯ: рекорд не читается и не пишется здесь. Сохранённый рекорд
 * приходит параметром `bestSoFar`, запись — `saveBestStreak()` из эффекта
 * (раньше `writeBest` дёргался прямо из render path — долг `TODO.md §7`).
 */
export function computeRecordingStreak(
  operations: Operation[],
  frozen: Set<string> = new Set(),
  now: Date = new Date(),
  aggregates?: Pick<Aggregates, 'recorded_days'> | null,
  bestSoFar = 0,
): RecordingStreak {
  const days = recordedDaySet(operations, aggregates);
  const today = toLocalIso(now);
  const todayDone = days.has(today);

  let current = 0;
  let cursor = todayDone ? today : shiftIso(today, -1);
  for (let i = 0; i < 3650; i++) {
    if (!days.has(cursor) && !frozen.has(cursor)) break;
    current++;
    cursor = shiftIso(cursor, -1);
  }

  return { current, best: Math.max(current, bestSoFar), todayDone };
}

export interface HeatDay {
  iso: string;
  dayNum: number;
  recorded: boolean;
  /** recorded И расходы этой даты ≤ дневного бюджета. */
  underBudget: boolean;
  /** День закрыт ❄ заморозкой. */
  frozen: boolean;
  future: boolean;
  today: boolean;
}

/** Состояние клеток хитмапа текущего месяца. */
export function monthHeatmap(
  operations: Operation[],
  categories: Category[],
  settings: Settings | null,
  frozen: Set<string> = new Set(),
  now: Date = new Date(),
): HeatDay[] {
  const recorded = recordedDaySet(operations);
  const dailyBudget = computeDailyBudget(categories, settings, now);

  // только повседневные траты — крупные нерегулярные не должны красить день
  const catByName = new Map(categories.map((c) => [c.name, c] as const));
  const spentByDay = new Map<string, number>();
  for (const op of operations) {
    const cat = catByName.get(op.category);
    const t = cat?.type ?? op.type;
    if (t !== 'Расход') continue;
    if (cat && !cat.is_daily) continue;
    spentByDay.set(op.date, (spentByDay.get(op.date) || 0) + op.amount);
  }

  const y = now.getFullYear();
  const m = now.getMonth();
  const totalDays = new Date(y, m + 1, 0).getDate();
  const today = toLocalIso(now);

  const out: HeatDay[] = [];
  for (let d = 1; d <= totalDays; d++) {
    const iso = toLocalIso(new Date(y, m, d));
    const rec = recorded.has(iso);
    out.push({
      iso,
      dayNum: d,
      recorded: rec,
      underBudget: rec && (spentByDay.get(iso) || 0) <= dailyBudget,
      frozen: !rec && frozen.has(iso),
      future: iso > today,
      today: iso === today,
    });
  }
  return out;
}

// ---- localStorage для best ----
//
// Хранилище отделено от расчёта: читать — при монтировании компонента,
// писать — из useEffect. В render path побочных эффектов быть не должно.

const BEST_KEY = 'recording_streak_best';

/** Сохранённый рекорд стрика. Хранилища нет / мусор → 0. */
export function readBestStreak(): number {
  try {
    const v = localStorage.getItem(BEST_KEY);
    return v ? Number(v) || 0 : 0;
  } catch {
    return 0;
  }
}

/** Сохранить рекорд, если он выше сохранённого. Только из эффекта, не из render. */
export function saveBestStreak(n: number): void {
  if (n <= readBestStreak()) return;
  try { localStorage.setItem(BEST_KEY, String(n)); } catch { /* ignore */ }
}
